import { defineAsyncComponent, type Component } from 'vue';
import type { User } from '@/types/User';
import { IsUserAnAdmin, IsUserABroker } from '@/utilities/AuthUtils';
import { DEFAULT_PROGRAMS_EXPIRING_CONFIG, programsExpiringTitle, resolveProgramsExpiringConfig } from './widgets/programsExpiringTable';

export interface WidgetDefinition {
  component: Component;
  settingsComponent?: Component;
  displayName: string;
  description: string;
  defaultColSpan: number;
  defaultConfig: Record<string, unknown> | null;
  /** When true the widget card chrome (border, shadow, title) is hidden outside edit mode. */
  chromeless?: boolean;
  titleFn?: (config: Record<string, unknown>) => string;
  /** Optional access check — when provided the widget is only shown if this returns true. */
  canAccess?: (user: User | undefined) => boolean;
}

const widgetRegistry: Record<string, WidgetDefinition> = {
  'programs-expiring': {
    component: defineAsyncComponent(() => import('./widgets/ProgramsExpiringWidget.vue')),
    settingsComponent: defineAsyncComponent(() => import('./widgets/settings/ProgramsExpiringSettings.vue')),
    displayName: 'Expiring Programs',
    description:
      'Shows programs expiring within 30, 60, 90, or 120 days on my favorited accounts, programs where I have a servicing role, or both.',
    defaultColSpan: 1,
    defaultConfig: { ...DEFAULT_PROGRAMS_EXPIRING_CONFIG },
    titleFn: (config) => programsExpiringTitle(resolveProgramsExpiringConfig(config).days),
  },
  'my-platform-clients': {
    component: defineAsyncComponent(() => import('./widgets/MyPlatformClientsWidget.vue')),
    displayName: 'Clients with Platform Access',
    description: 'Shows my accounts that clients can currently access.',
    defaultColSpan: 1,
    defaultConfig: null,
  },
  'my-platform-notifications': {
    component: defineAsyncComponent(() => import('./widgets/MyPlatformNotificationsWidget.vue')),
    displayName: 'Notifications',
    description: 'Shows my latest unread platform notifications.',
    defaultColSpan: 1,
    defaultConfig: null,
  },
  'top-clients-by-revenue': {
    component: defineAsyncComponent(() => import('./widgets/TopClientsByRevenueWidget.vue')),
    displayName: 'Top 10 Clients By Revenue',
    description: 'Chart total revenue by client for policies where I have a servicing role.',
    defaultColSpan: 2,
    defaultConfig: null,
  },
  'market-list': {
    component: defineAsyncComponent(() => import('./widgets/MarketListWidget.vue')),
    settingsComponent: defineAsyncComponent(() => import('./widgets/settings/MarketListSettings.vue')),
    displayName: 'My Favorite Markets',
    description: 'A custom list of carriers and wholesaler groups with links to their overview pages.',
    defaultColSpan: 1,
    defaultConfig: { title: '', description: '', syncWithFavorites: true, selectedMarkets: [] },
    titleFn: (config) => (typeof config.title === 'string' && config.title.length > 0 ? config.title : 'My Favorite Markets'),
  },
  'dashboard-list': {
    component: defineAsyncComponent(() => import('./widgets/DashboardListWidget.vue')),
    settingsComponent: defineAsyncComponent(() => import('./widgets/settings/DashboardListSettings.vue')),
    displayName: 'My Favorite Dashboards',
    description: 'A custom list of favorited Tableau dashboards from Reporting and Analytics.',
    defaultColSpan: 1,
    defaultConfig: { title: '', description: '', syncWithFavorites: true, selectedDashboards: [] },
    titleFn: (config) => (typeof config.title === 'string' && config.title.length > 0 ? config.title : 'My Favorite Dashboards'),
  },
  'ai-data-extraction': {
    component: defineAsyncComponent(() => import('./widgets/AIDataExtractionWidget.vue')),
    displayName: 'AI Data Extraction Results',
    description: 'Shows recent AI data extraction results for your servicing policies and favorited accounts.',
    defaultColSpan: 2,
    defaultConfig: null,
    canAccess: (user) => IsUserAnAdmin(user) || IsUserABroker(user),
  },
  'recently-visited-accounts': {
    component: defineAsyncComponent(() => import('./widgets/RecentlyVisitedWidget.vue')),
    displayName: 'Recently Visited Accounts',
    description: 'The accounts you have opened most recently.',
    defaultColSpan: 1,
    defaultConfig: { entityType: 'account' },
    canAccess: (user) => IsUserAnAdmin(user) || IsUserABroker(user),
  },
  'servicing-kpi': {
    component: defineAsyncComponent(() => import('./widgets/ServicingKpiWidget.vue')),
    displayName: 'Servicing Stats',
    description: 'Key metrics for your servicing book: clients, policies, premium, and revenue.',
    defaultColSpan: 2,
    defaultConfig: null,
  },
  'nav-link-group': {
    component: defineAsyncComponent(() => import('./widgets/NavLinkGroupWidget.vue')),
    settingsComponent: defineAsyncComponent(() => import('./widgets/settings/NavLinkGroupSettings.vue')),
    displayName: 'Navigation Link Group',
    description: 'A customizable group of navigation links with a title and description.',
    defaultColSpan: 1,
    defaultConfig: { title: '', description: '', selectedLinks: [] },
    titleFn: (config) => (typeof config.title === 'string' && config.title.length > 0 ? config.title : 'Navigation Links'),
  },
};

export default widgetRegistry;
